// FormModel.js

/**
 * FormModel.js
 *
 * Document-shape helpers for type-scoped form templates (_forms).
 */

import { Timestamp } from 'firebase/firestore';

import { withBaseKey } from 'utilities/basePersistence';
import { generateKey } from 'utilities/Keys';
import { buildDefaultLayoutTree, ensureLayoutHasAssignedBlock } from 'services/layouts/LayoutModel';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

export const DEFAULT_FORM_KEY = 'details';
export const DEFAULT_FORM_TITLE = 'Details';
export const DEFAULT_FORM_DESCRIPTION = 'Default metadata form for this type.';
export const DEFAULT_CATCH_ALL_BLOCK_TITLE = 'Other fields';
export const FORM_FIELD_BLOCK_TYPE = 'FIELD';
export const FORM_CATCH_ALL_BLOCK_KEY = '__catch_all__';

/**
 * Field types managed outside form bodies (workspace/peek chrome, etc.).
 * Excluded from form layouts, sync, and form field rendering.
 */
export const FORM_EXCLUDED_FIELD_TYPES = new Set([
  'cover',
  'icon',
]);

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getFieldType(fieldDef) {
  return normalizeString(fieldDef?.type).toLowerCase();
}

/**
 * Whether a schema field type should be omitted from form templates/rendering.
 *
 * @param {string} fieldType
 * @returns {boolean}
 */
export function isFormExcludedFieldType(fieldType) {
  return FORM_EXCLUDED_FIELD_TYPES.has(normalizeString(fieldType).toLowerCase());
}

/**
 * Builds a stable Firestore document id for a form template.
 */
export function buildFormDocId({
  baseKey,
  typeKey,
  key = DEFAULT_FORM_KEY,
} = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedKey = normalizeString(key) || DEFAULT_FORM_KEY;

  if (!cleanedBaseKey || !cleanedTypeKey) {
    return cleanedKey;
  }

  return `${cleanedBaseKey}__${cleanedTypeKey}__${cleanedKey}`;
}

function getFieldKey(fieldDef) {
  return normalizeString(fieldDef?.name || fieldDef?.key);
}

/**
 * Returns visible schema field keys suitable for form templates.
 * Omits types managed outside the form body (e.g. cover via Add Cover Photo).
 *
 * @param {object|null} typeDef
 * @returns {string[]}
 */
export function getVisibleFormFieldKeys(typeDef) {
  return resolveDisplaySchemaFields(typeDef, { includeHidden: false })
    .filter((fieldDef) => !isFormExcludedFieldType(getFieldType(fieldDef)))
    .map((fieldDef) => getFieldKey(fieldDef))
    .filter(Boolean);
}

/**
 * Builds the bootstrap layout scaffold for the default Details form.
 *
 * @param {object} params
 * @param {string[]} [params.fieldKeys]
 * @returns {{ layout: object, blocksByKey: object }}
 */
export function buildDefaultFormLayoutScaffold({ fieldKeys = [] } = {}) {
  const cleanedFieldKeys = Array.isArray(fieldKeys)
    ? fieldKeys.map((key) => normalizeString(key)).filter(Boolean)
    : [];

  if (!cleanedFieldKeys.length) {
    return ensureLayoutHasAssignedBlock({
      blocksByKey: {},
      layout: buildDefaultLayoutTree(null),
    });
  }

  const blocksByKey = {};
  const rowKeys = [];
  const nodes = {
    layout_root: {
      children: rowKeys,
      key: 'layout_root',
      kind: 'stack',
    },
  };

  cleanedFieldKeys.forEach((fieldKey) => {
    const blockKey = generateKey();
    const rowKey = generateKey();
    const slotKey = generateKey();

    blocksByKey[blockKey] = {
      fieldKeys: [fieldKey],
      key: blockKey,
      titleOverride: null,
      type: FORM_FIELD_BLOCK_TYPE,
    };

    rowKeys.push(rowKey);
    nodes[rowKey] = {
      children: [slotKey],
      key: rowKey,
      kind: 'row',
    };
    nodes[slotKey] = {
      blockKey,
      key: slotKey,
      kind: 'block_slot',
    };
  });

  return ensureLayoutHasAssignedBlock({
    blocksByKey,
    layout: {
      nodes,
      rootNodeKey: 'layout_root',
    },
  });
}

export function normalizeFormRow(docSnap) {
  const data = docSnap.data() || {};

  return {
    id: docSnap.id,
    ...data,
    key: data?.key || docSnap.id,
  };
}

/**
 * Builds a Firestore payload for a form template document.
 *
 * @param {object} params
 * @returns {object|null}
 */
export function buildFormPayload({
  key,
  baseKey,
  typeKey,
  userKey,
  title,
  description = '',
  position = null,
  isDefault = false,
  layoutEdited = false,
  layout = null,
  blocksByKey = null,
} = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedUserKey = normalizeString(userKey);

  if (!cleanedBaseKey || !cleanedTypeKey || !cleanedUserKey) {
    return null;
  }

  const formKey = normalizeString(key) || generateKey();
  const resolvedTitle = normalizeString(title) || DEFAULT_FORM_TITLE;
  const resolvedDescription = normalizeString(description);
  const numericPosition = Number(position);
  const { blocksByKey: resolvedBlocksByKey, layout: resolvedLayout } = ensureLayoutHasAssignedBlock({
    blocksByKey,
    layout,
  });
  const now = Timestamp.now();

  return withBaseKey({
    blocksByKey: resolvedBlocksByKey,
    dateCreated: now,
    dateModified: now,
    description: resolvedDescription,
    isDefault: Boolean(isDefault),
    key: formKey,
    layout: resolvedLayout,
    layoutEdited: Boolean(layoutEdited),
    title: resolvedTitle,
    titleLower: resolvedTitle.toLowerCase(),
    typeKey: cleanedTypeKey,
    createdBy: cleanedUserKey,
    updatedBy: cleanedUserKey,
    ...(Number.isFinite(numericPosition) ? { position: numericPosition } : {}),
  }, cleanedBaseKey);
}

/**
 * Builds the default Details form payload for a newly created type.
 *
 * @param {object} params
 * @param {object|null} params.typeDef
 * @returns {object|null}
 */
export function buildDefaultDetailsFormPayload({
  baseKey,
  typeKey,
  userKey,
  typeDef = null,
} = {}) {
  const fieldKeys = getVisibleFormFieldKeys(typeDef);
  const { blocksByKey, layout } = buildDefaultFormLayoutScaffold({ fieldKeys });

  return buildFormPayload({
    blocksByKey,
    description: DEFAULT_FORM_DESCRIPTION,
    isDefault: true,
    key: DEFAULT_FORM_KEY,
    layout,
    layoutEdited: false,
    position: 0,
    title: DEFAULT_FORM_TITLE,
    typeKey,
    userKey,
    baseKey,
  });
}

/**
 * Collects all field keys referenced by FIELD blocks in a form document.
 *
 * @param {object|null} formDoc
 * @returns {string[]}
 */
export function collectFormFieldKeys(formDoc) {
  const blocksByKey = formDoc?.blocksByKey && typeof formDoc.blocksByKey === 'object'
    ? formDoc.blocksByKey
    : {};

  const keys = new Set();

  Object.values(blocksByKey).forEach((block) => {
    const blockType = normalizeString(block?.type).toUpperCase();
    if (blockType !== FORM_FIELD_BLOCK_TYPE && blockType !== 'FIELDS') return;

    (Array.isArray(block?.fieldKeys) ? block.fieldKeys : []).forEach((fieldKey) => {
      const cleaned = normalizeString(fieldKey);
      if (cleaned) keys.add(cleaned);
    });
  });

  return Array.from(keys);
}

/**
 * Walks a form layout tree and returns FIELD block keys in display order.
 *
 * @param {object|null} formDoc
 * @returns {string[]}
 */
export function collectFormFieldKeysInLayoutOrder(formDoc) {
  const layout = formDoc?.layout && typeof formDoc.layout === 'object'
    ? formDoc.layout
    : null;
  const blocksByKey = formDoc?.blocksByKey && typeof formDoc.blocksByKey === 'object'
    ? formDoc.blocksByKey
    : {};

  if (!layout?.nodes || typeof layout.nodes !== 'object') {
    return collectFormFieldKeys(formDoc);
  }

  const orderedKeys = [];
  const seen = new Set();

  const visit = (nodeKey) => {
    const layoutNode = layout.nodes?.[nodeKey];
    if (!layoutNode) return;

    if (layoutNode.kind === 'block_slot') {
      const blockKey = normalizeString(layoutNode.blockKey);
      const block = blockKey ? blocksByKey[blockKey] : null;
      const blockType = normalizeString(block?.type).toUpperCase();
      if (blockType !== FORM_FIELD_BLOCK_TYPE && blockType !== 'FIELDS') return;

      (Array.isArray(block?.fieldKeys) ? block.fieldKeys : []).forEach((fieldKey) => {
        const cleaned = normalizeString(fieldKey);
        if (!cleaned || seen.has(cleaned)) return;
        seen.add(cleaned);
        orderedKeys.push(cleaned);
      });
      return;
    }

    const children = Array.isArray(layoutNode.children) ? layoutNode.children : [];
    children.forEach((childKey) => visit(childKey));
  };

  visit(normalizeString(layout.rootNodeKey) || 'layout_root');

  // Include any orphaned block fields that are not reachable from layout.
  collectFormFieldKeys(formDoc).forEach((fieldKey) => {
    if (seen.has(fieldKey)) return;
    seen.add(fieldKey);
    orderedKeys.push(fieldKey);
  });

  return orderedKeys;
}

/**
 * Syncs the default Details form with the current type schema.
 *
 * - Unedited defaults are rebuilt from schema field order.
 * - Edited defaults only append newly added fields to a catch-all block.
 *
 * @param {object|null} formDoc
 * @param {object|null} typeDef
 * @returns {object|null} Updated form doc or null when unchanged.
 */
export function appendMissingDefaultFormFields(formDoc, typeDef) {
  if (!formDoc?.isDefault) return null;

  const expectedKeys = getVisibleFormFieldKeys(typeDef);

  if (!formDoc?.layoutEdited) {
    const presentOrderedKeys = collectFormFieldKeysInLayoutOrder(formDoc);
    const unchanged = presentOrderedKeys.length === expectedKeys.length
      && presentOrderedKeys.every((key, index) => key === expectedKeys[index]);

    if (unchanged) return null;

    const bootstrap = buildDefaultFormLayoutScaffold({ fieldKeys: expectedKeys });
    return {
      ...formDoc,
      blocksByKey: bootstrap.blocksByKey,
      layout: bootstrap.layout,
    };
  }

  const presentKeys = new Set(collectFormFieldKeys(formDoc));
  const missingKeys = expectedKeys.filter((key) => !presentKeys.has(key));

  if (!missingKeys.length) return null;

  const blocksByKey = formDoc?.blocksByKey && typeof formDoc.blocksByKey === 'object'
    ? { ...formDoc.blocksByKey }
    : {};
  const layout = formDoc?.layout && typeof formDoc.layout === 'object'
    ? JSON.parse(JSON.stringify(formDoc.layout))
    : buildDefaultFormLayoutScaffold({ fieldKeys: expectedKeys }).layout;

  let catchAllBlockKey = Object.entries(blocksByKey).find(([, block]) => (
    normalizeString(block?.key) === FORM_CATCH_ALL_BLOCK_KEY
    || normalizeString(block?.titleOverride).toLowerCase() === DEFAULT_CATCH_ALL_BLOCK_TITLE.toLowerCase()
  ))?.[0] || '';

  if (!catchAllBlockKey) {
    catchAllBlockKey = generateKey();
    blocksByKey[catchAllBlockKey] = {
      fieldKeys: [],
      key: catchAllBlockKey,
      titleOverride: DEFAULT_CATCH_ALL_BLOCK_TITLE,
      type: FORM_FIELD_BLOCK_TYPE,
    };

    const rootNodeKey = normalizeString(layout?.rootNodeKey) || 'layout_root';
    const rootNode = layout?.nodes?.[rootNodeKey];
    const stackChildKey = Array.isArray(rootNode?.children) ? rootNode.children[0] : null;
    const stackNode = stackChildKey ? layout?.nodes?.[stackChildKey] : null;

    if (stackNode?.kind === 'stack') {
      const slotKey = generateKey();
      layout.nodes = {
        ...layout.nodes,
        [slotKey]: {
          blockKey: catchAllBlockKey,
          key: slotKey,
          kind: 'block_slot',
        },
        [stackChildKey]: {
          ...stackNode,
          children: [...(Array.isArray(stackNode.children) ? stackNode.children : []), slotKey],
        },
      };
    }
  }

  const catchAllBlock = blocksByKey[catchAllBlockKey] || {
    fieldKeys: [],
    key: catchAllBlockKey,
    titleOverride: DEFAULT_CATCH_ALL_BLOCK_TITLE,
    type: FORM_FIELD_BLOCK_TYPE,
  };

  blocksByKey[catchAllBlockKey] = {
    ...catchAllBlock,
    fieldKeys: [
      ...(Array.isArray(catchAllBlock.fieldKeys) ? catchAllBlock.fieldKeys : []),
      ...missingKeys,
    ],
  };

  return {
    ...formDoc,
    blocksByKey,
    layout,
  };
}

/**
 * Removes invalid field keys from all FIELD blocks in a form document.
 *
 * @param {object|null} formDoc
 * @param {Set<string>|string[]} allowedFieldKeys
 * @returns {object|null}
 */
export function pruneFormFieldKeys(formDoc, allowedFieldKeys) {
  if (!formDoc) return null;

  const allowed = allowedFieldKeys instanceof Set
    ? allowedFieldKeys
    : new Set((Array.isArray(allowedFieldKeys) ? allowedFieldKeys : []).map((key) => normalizeString(key)).filter(Boolean));

  const blocksByKey = formDoc?.blocksByKey && typeof formDoc.blocksByKey === 'object'
    ? { ...formDoc.blocksByKey }
    : {};

  let changed = false;

  Object.entries(blocksByKey).forEach(([blockKey, block]) => {
    const blockType = normalizeString(block?.type).toUpperCase();
    if (blockType !== FORM_FIELD_BLOCK_TYPE && blockType !== 'FIELDS') return;

    const nextFieldKeys = (Array.isArray(block?.fieldKeys) ? block.fieldKeys : [])
      .map((fieldKey) => normalizeString(fieldKey))
      .filter((fieldKey) => fieldKey && allowed.has(fieldKey));

    if (nextFieldKeys.length !== (block?.fieldKeys || []).length) {
      changed = true;
    }

    if (!nextFieldKeys.length) {
      delete blocksByKey[blockKey];
      changed = true;
      return;
    }

    blocksByKey[blockKey] = {
      ...block,
      fieldKeys: nextFieldKeys,
    };
  });

  if (!changed) return null;

  return {
    ...formDoc,
    blocksByKey,
  };
}
